// The expense report (ADR-0035, under Money): a month's expenses by expense account under its statement group, against
// the values of one of the business's Dimensions and the unallocated, with row and column totals, read from the Ledgers
// each time. Every figure opens to the entries behind it, each entry to its Document as the person's Roles show it, and
// the report downloads as a sheet. A month with nothing says so; a part the person's Roles hide (payroll) is said too.
import { useEffect, useState } from "react";
import { rupees } from "../../api.js";
import { ApiError } from "../../http.js";
import { useWords } from "../../i18n/words.js";
import {
  createExpensesApi,
  sheetHref,
  type ExpenseDocument,
  type ExpenseEntries,
  type ExpenseFigure,
  type ExpenseReport,
  type ExpensesApi,
  type FigureAsked,
} from "./api.js";

const pad = (n: number) => String(n).padStart(2, "0");

/** The month `date` falls in, as YYYY-MM. */
export const monthOf = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;

/** A month (YYYY-MM) as the period from its first day to its last. */
export function monthPeriod(month: string): { from: string; to: string } {
  const [year = 0, number = 1] = month.split("-").map(Number);
  const last = new Date(Date.UTC(year, number, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${pad(last)}` };
}

/** A figure opened: what it is, in words, and the entries behind it. */
interface Opened {
  readonly title: string;
  readonly found?: ExpenseEntries;
  readonly problem?: string;
}

const problemOf = (error: unknown, fallback: string) =>
  error instanceof ApiError && error.problems.length > 0 ? error.problems.join(" ") : fallback;

/** A Document's own fields, as plain words: the scalar ones, without ids. */
const shownFields = (fields: Readonly<Record<string, unknown>>) =>
  Object.entries(fields).filter(
    ([key, value]) =>
      key !== "id" &&
      !key.endsWith("_id") &&
      (typeof value === "string" || typeof value === "number" || typeof value === "boolean"),
  );
const linesOf = (fields: Readonly<Record<string, unknown>>): Readonly<Record<string, unknown>>[] =>
  Array.isArray(fields["lines"])
    ? fields["lines"].filter((l): l is Record<string, unknown> => typeof l === "object" && l !== null)
    : [];

function DocumentPanel({ document, onClose }: { document: ExpenseDocument; onClose: () => void }) {
  const w = useWords();
  const lines = linesOf(document.fields);
  const columns = [...new Set(lines.flatMap((l) => shownFields(l).map(([key]) => key)))];
  return (
    <section className="expense-document" aria-label={w.expensesDocumentHeading}>
      <h3>{w.expensesDocumentHeading}</h3>
      <dl>
        <dt>{w.expensesKind}</dt>
        <dd>{document.object.replaceAll("_", " ")}</dd>
        {shownFields(document.fields).map(([key, value]) => (
          <div key={key}>
            <dt>{key.replaceAll("_", " ")}</dt>
            <dd>{String(value)}</dd>
          </div>
        ))}
      </dl>
      {lines.length > 0 ? (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c}>{c.replaceAll("_", " ")}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <td key={c}>{line[c] === undefined ? "" : String(line[c])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <button type="button" onClick={onClose}>
        {w.expensesClose}
      </button>
    </section>
  );
}

const defaultApi = createExpensesApi();

export function Expenses({ api = defaultApi, today = () => new Date() }: { api?: ExpensesApi; today?: () => Date }) {
  const w = useWords();
  const [month, setMonth] = useState(() => monthOf(today()));
  const [dimension, setDimension] = useState<string | undefined>(undefined);
  const [report, setReport] = useState<ExpenseReport | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [opened, setOpened] = useState<Opened | undefined>(undefined);
  const [document, setDocument] = useState<ExpenseDocument | undefined>(undefined);
  const [documentProblem, setDocumentProblem] = useState<string | undefined>(undefined);
  const period = monthPeriod(month);

  useEffect(() => {
    let live = true;
    setReport(undefined);
    setProblem(undefined);
    setOpened(undefined);
    setDocument(undefined);
    api.expenses({ ...period, dimension }).then(
      (found) => live && setReport(found),
      (error: unknown) => live && setProblem(problemOf(error, w.expensesLoadFailed)),
    );
    return () => {
      live = false;
    };
  }, [api, month, dimension]);

  const columnName = (value: string | null) =>
    value === null ? w.expensesUnallocated : (report?.columns.find((c) => c.value === value)?.name ?? value);

  function open(title: string, figure: Omit<FigureAsked, "from" | "to" | "dimension">) {
    if (!report) return;
    setOpened({ title });
    setDocument(undefined);
    setDocumentProblem(undefined);
    api.entries({ ...period, dimension: report.dimension, ...figure }).then(
      (found) => setOpened({ title, found }),
      (error: unknown) => setOpened({ title, problem: problemOf(error, w.expensesLoadFailed) }),
    );
  }

  function openDocument(object: string, id: string) {
    setDocumentProblem(undefined);
    api.document(object, id).then(setDocument, (error: unknown) => {
      setDocument(undefined);
      setDocumentProblem(problemOf(error, w.expensesDocumentRefused));
    });
  }

  /** A figure as a button opening its entries; a zero opens nothing. */
  const figureButton = (amount: number, title: string, figure: Omit<FigureAsked, "from" | "to" | "dimension">) =>
    amount === 0 ? (
      <span className="muted">{rupees(0)}</span>
    ) : (
      <button type="button" className="linkish" aria-label={title} onClick={() => open(title, figure)}>
        {rupees(amount)}
      </button>
    );
  const cells = (byValue: readonly ExpenseFigure[], row: string, account?: string) =>
    byValue.map((f) => (
      <td key={String(f.value)} className="amount">
        {figureButton(f.amount, w.expensesFigure(row, columnName(f.value)), { account, value: f.value })}
      </td>
    ));

  const controls = (
    <form className="expense-controls" onSubmit={(event) => event.preventDefault()}>
      <label>
        {w.expensesMonth}{" "}
        <input
          type="month"
          value={month}
          required
          onChange={(event) => event.target.value && setMonth(event.target.value)}
        />
      </label>{" "}
      {report && report.dimensions.length > 1 ? (
        <label>
          {w.expensesSplitBy}{" "}
          <select value={report.dimension} onChange={(event) => setDimension(event.target.value)}>
            {report.dimensions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}{" "}
      {report && report.groups.length > 0 ? (
        <a href={sheetHref({ ...period, dimension: report.dimension })} download>
          {w.expensesDownload}
        </a>
      ) : null}
    </form>
  );

  return (
    <>
      <div className="hello">
        <h2>{report ? w.expensesHeading(report.label) : w.pageExpenses}</h2>
        <p className="muted">{w.expensesIntro}</p>
      </div>
      {controls}
      {!report ? (
        problem ? (
          <p role="alert">{problem}</p>
        ) : (
          <p role="status">{w.expensesLoading}</p>
        )
      ) : (
        <>
          {report.hidden?.includes("payroll") ? <p className="muted">{w.expensesPayrollHidden}</p> : null}
          {report.groups.length === 0 ? (
            <p>{w.expensesNothing(report.from, report.to)}</p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl expense-report">
                <thead>
                  <tr>
                    <th scope="col">{w.expensesAccount}</th>
                    {report.columns.map((c) => (
                      <th key={String(c.value)} scope="col">
                        {columnName(c.value)}
                      </th>
                    ))}
                    <th scope="col">{w.expensesTotal}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.groups.map((g) => [
                    <tr key={`group:${g.group}`} className="expense-group">
                      <th scope="row">{g.group}</th>
                      {g.byValue.map((f) => (
                        <td key={String(f.value)} className="amount">
                          {rupees(f.amount)}
                        </td>
                      ))}
                      <td className="amount">{rupees(g.amount)}</td>
                    </tr>,
                    ...g.accounts.map((a) => (
                      <tr key={a.account}>
                        <th scope="row">{a.name}</th>
                        {cells(a.byValue, a.name, a.account)}
                        <td className="amount">
                          {figureButton(a.amount, w.expensesFigure(a.name, w.expensesTotal), { account: a.account })}
                        </td>
                      </tr>
                    )),
                  ])}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">{w.expensesTotal}</th>
                    {cells(report.byValue, w.expensesTotal)}
                    <td className="amount">
                      {figureButton(report.total, w.expensesFigure(w.expensesTotal, w.expensesTotal), {})}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
      {opened ? (
        <section className="expense-entries" aria-label={opened.title}>
          <h3>{opened.title}</h3>
          {opened.problem ? (
            <p role="alert">{opened.problem}</p>
          ) : !opened.found ? (
            <p role="status">{w.expensesLoading}</p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th scope="col">{w.expensesDate}</th>
                    <th scope="col">{w.expensesDocument}</th>
                    <th scope="col">{w.expensesSupplier}</th>
                    <th scope="col">{w.expensesAmount}</th>
                  </tr>
                </thead>
                <tbody>
                  {opened.found.entries.map((e, i) => {
                    const named = `${e.document.object.replaceAll("_", " ")} ${e.document.number ?? ""}`.trim();
                    const found = opened.found;
                    return (
                      <tr key={`${e.posting}:${i}`}>
                        <td>{e.date}</td>
                        <td>
                          <button
                            type="button"
                            className="linkish"
                            onClick={() => openDocument(e.document.object, e.document.id)}
                          >
                            {named}
                          </button>
                        </td>
                        <td>{e.counterparty ? (found?.parties[e.counterparty] ?? "") : ""}</td>
                        <td className="amount">{rupees(e.amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {opened.found?.hidden?.includes("payroll") ? <p className="muted">{w.expensesPayrollHidden}</p> : null}
        </section>
      ) : null}
      {documentProblem ? <p role="alert">{documentProblem}</p> : null}
      {document ? <DocumentPanel document={document} onClose={() => setDocument(undefined)} /> : null}
    </>
  );
}
